import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { getSwingShadow } from "@/lib/swing-shadow.functions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute("/_authenticated/admin/swing-shadow")({
  head: () => ({
    meta: [
      { title: "OSAS-01 Strategic Engine | Admin" },
      { name: "description", content: "Admin view of OSAS-01: D1 regime context, open swing position, closed trades and rejection analytics." },
      { property: "og:title", content: "OSAS-01 Strategic Engine" },
      { property: "og:description", content: "Swing engine record under Orion AI. D1 regime, H4 pullback, ATR-scaled exits." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SwingShadowPage,
});

const fmt = (s: number) => new Date(s * 1000).toISOString().slice(0, 16).replace("T", " ");
const px = (x: number) => x.toFixed(2);
const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{children}</p>
);
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`rounded-lg border border-border/60 bg-card p-4 ${className}`}>{children}</div>
);
function Rows({ items }: { items: [string, React.ReactNode][] }) {
  return (
    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 font-mono text-xs sm:grid-cols-4">
      {items.map(([k, v]) => (
        <div key={k}>
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="mt-0.5">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function SwingShadowPage() {
  const run = useServerFn(getSwingShadow);
  const q = useQuery({ queryKey: ["swing-shadow"], queryFn: () => run({ data: {} }), refetchInterval: 5 * 60_000 });
  const r = q.data;
  const c = r?.context ?? null;

  const regimeText = !c
    ? { t: "Waiting for D1 warmup", tone: "text-muted-foreground", why: "D1 SMA 200 or ATR 14 not yet available." }
    : c.regime === "long"
      ? { t: "Looking for LONG pullbacks", tone: "text-emerald", why: `Last D1 close ${px(c.d1Close)} is above SMA 50 (${px(c.d1Sma50)}) and SMA 200 (${px(c.d1Sma200)}).` }
      : c.regime === "short"
        ? { t: "Looking for SHORT pullbacks", tone: "text-destructive", why: `Last D1 close ${px(c.d1Close)} is below SMA 50 (${px(c.d1Sma50)}) and SMA 200 (${px(c.d1Sma200)}).` }
        : { t: "Flat: no aligned regime", tone: "text-gold", why: `Last D1 close ${px(c.d1Close)} sits between SMA 50 (${px(c.d1Sma50)}) and SMA 200 (${px(c.d1Sma200)}). No entries.` };

  const rejected = r?.summary.rejected ?? {};
  const rejTotal = Object.values(rejected).reduce((a, b) => a + b, 0);
  const signals = (r?.summary.trades ?? 0) + (r?.open ? 1 : 0) + rejTotal;

  return (
    <div className="space-y-6">
      <div>
        <Label>Orion AI · OSAS-01 Strategic · ORION-SW-01 lineage</Label>
        <h1 className="font-display text-2xl font-bold">OSAS-01 Strategic Engine</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Swing engine: D1 regime, H4 pullback entry, ATR-scaled exits. Holds for days and through weekends.
        </p>
      </div>

      {q.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Replaying live candles.
        </div>
      )}
      {q.error && <p className="text-sm text-destructive">{(q.error as Error).message}</p>}

      {r && (
        <>
          <Card className="border-gold/30">
            <Label>Directional context · D1 regime</Label>
            <p className={`mt-2 font-display text-2xl font-bold ${regimeText.tone}`}>{regimeText.t}</p>
            <p className="mt-1 text-sm text-muted-foreground">{regimeText.why}</p>
            {c && (
              <Rows
                items={[
                  ["Latest price", px(c.lastPrice)],
                  ["D1 SMA 50", px(c.d1Sma50)],
                  ["D1 SMA 200", px(c.d1Sma200)],
                  ["H4 SMA 50 (entry line)", c.h4Sma50 != null ? px(c.h4Sma50) : "n/a"],
                  ["D1 ATR 14", `$${px(c.atr14)}`],
                  ["D1 close used", `${fmt(c.d1CloseAt)} UTC`],
                  ["Last bar", `${fmt(r.lastBarS)} UTC`],
                ]}
              />
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Acceptance context only, not a trade signal. A completed H4 rejection of the H4 SMA 50 and a stop inside the ATR band are still required.
            </p>
          </Card>

          <div className="grid gap-4 sm:grid-cols-3">
            <Card>
              <Label>Engine state</Label>
              <p className={`mt-2 font-mono text-sm ${r.armed ? "text-emerald" : "text-gold"}`}>{r.armed ? "ARMED" : "NOT ARMED"}</p>
              <p className="mt-1 text-xs text-muted-foreground">{r.armed ? `Recording since ${fmt(r.armedFromS!)} UTC` : "No forward record."}</p>
            </Card>
            <Card>
              <Label>Today's geometry</Label>
              {c ? (
                <div className="mt-2 space-y-1 font-mono text-xs">
                  <p>Stop band ${px(c.stopBandLo)} to ${px(c.stopBandHi)}</p>
                  <p>TP1 ${px(c.tp1Distance)} · TP2 ${px(c.tp2Distance)}</p>
                </div>
              ) : <p className="mt-2 text-xs text-muted-foreground">Awaiting ATR.</p>}
            </Card>
            <Card>
              <Label>Book</Label>
              <p className="mt-2 font-mono text-sm">{r.open ? `1 open · ${r.open.direction.toUpperCase()}` : "Flat"}</p>
              <p className="mt-1 text-xs text-muted-foreground">One swing position at a time.</p>
            </Card>
          </div>

          <Tabs defaultValue="overview">
            <TabsList className="flex-wrap">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="trades">Closed trades</TabsTrigger>
              <TabsTrigger value="rejections">Rejections</TabsTrigger>
              <TabsTrigger value="rules">Frozen rules</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <Card>
                <Label>Open position</Label>
                {r.open ? (
                  <Rows
                    items={[
                      ["Opened", `${fmt(r.open.openedAt)} UTC`],
                      ["Side", r.open.direction.toUpperCase()],
                      ["Entry", px(r.open.entry)],
                      ["Stop", `${px(r.open.stop)}${r.open.tp1Hit ? " (BE)" : ""}`],
                      ["TP1", `${px(r.open.tp1)}${r.open.tp1Hit ? " (hit)" : ""}`],
                      ["TP2", px(r.open.tp2)],
                      ["Days held", ((r.lastBarS - r.open.openedAt) / 86400).toFixed(1)],
                      [
                        "Floating",
                        <span className={r.open.floatingR >= 0 ? "text-emerald" : "text-destructive"}>{r.open.floatingR.toFixed(2)}R</span>,
                      ],
                    ]}
                  />
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">No open swing position. Awaiting a qualified H4 pullback.</p>
                )}
              </Card>
              <Card>
                <Label>Record since arming</Label>
                <Rows
                  items={[
                    ["Closed trades", r.summary.trades],
                    ["Win rate", r.summary.trades ? `${(r.summary.winRate * 100).toFixed(1)}%` : "n/a"],
                    ["Net R", r.summary.netR.toFixed(2)],
                    ["Profit factor", r.summary.profitFactor?.toFixed(2) ?? "n/a"],
                    ["Max drawdown", `${r.summary.maxDrawdownR.toFixed(2)}R`],
                    ["Max loss streak", r.summary.maxLossStreak],
                    ["Avg hold", `${r.summary.avgHoldDays.toFixed(1)} days`],
                    ["Rejected signals", rejTotal],
                  ]}
                />
              </Card>
            </TabsContent>

            <TabsContent value="trades">
              <Card>
                {r.trades.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No closed trades yet.</p>
                ) : (
                  <div className="space-y-2">
                    {[...r.trades].reverse().map((t) => (
                      <div key={t.openedAt} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/40 px-3 py-2 font-mono text-xs">
                        <span className={t.direction === "long" ? "text-emerald" : "text-destructive"}>{t.direction.toUpperCase()}</span>
                        <span className="text-muted-foreground">{fmt(t.openedAt)} to {fmt(t.closedAt)}</span>
                        <span>Entry {px(t.entry)} · Stop ${px(t.stopDistance)}</span>
                        <span className="rounded border border-border/60 px-1.5">{t.outcome}</span>
                        <span className={t.r > 0 ? "text-emerald" : t.r < 0 ? "text-destructive" : ""}>{t.r.toFixed(2)}R</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="rejections">
              <Card>
                <Label>Why signals were refused</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  {signals} pullback signals since arming. {rejTotal} refused by stop geometry.
                </p>
                <div className="mt-3 space-y-2">
                  {[
                    ["<0.8atr", "Stop too tight (below 0.8 ATR): noise risk"],
                    [">1.6atr", "Stop too wide (above 1.6 ATR): oversized risk"],
                    ["bad", "Invalid stop (on the wrong side of entry)"],
                  ].map(([k, label]) => {
                    const v = rejected[k] ?? 0;
                    const pct = signals ? (v / signals) * 100 : 0;
                    return (
                      <div key={k}>
                        <div className="flex justify-between font-mono text-xs"><span>{label}</span><span>{v}</span></div>
                        <div className="mt-1 h-1.5 rounded bg-muted"><div className="h-1.5 rounded bg-gold" style={{ width: `${pct}%` }} /></div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="rules">
              <Card>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li><span className="text-foreground">Direction:</span> last D1 close above both SMA 50 and SMA 200 = long only; below both = short only; between = flat.</li>
                  <li><span className="text-foreground">Entry:</span> completed H4 candle rejects the H4 SMA 50 in trend direction; entry at next H4 open.</li>
                  <li><span className="text-foreground">Stop:</span> extreme of the last six H4 candles, accepted only between 0.8 and 1.6 times D1 ATR 14.</li>
                  <li><span className="text-foreground">Exits:</span> TP1 1.5 ATR (half closed, stop to entry), TP2 3 ATR.</li>
                  <li><span className="text-foreground">Book:</span> one position at a time, no session filters, holds through weekends.</li>
                </ul>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
      <footer className="border-t border-border/60 pt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        OSAS: Orion Strategic Allocation System · Swing engine under Orion AI
      </footer>
    </div>
  );
}
